import { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import ArticleIcon from '@mui/icons-material/Article';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { CustomButton, CustomTextArea, CustomTextField } from '../../../../common/components';
import { EMOJI_PRESETS } from '../constants/pushNotificationConstants';

const emphasisOptions = [
  { value: 'light', label: 'Liviano', icon: <TextFieldsIcon fontSize="small" /> },
  { value: 'regular', label: 'Medio', icon: <ArticleIcon fontSize="small" /> },
  { value: 'bold', label: 'Enfatizado', icon: <FormatBoldIcon fontSize="small" /> },
];

const quickSnippets = [
  'Recordá revisar tu panel de pagos.',
  'Tenemos una novedad sobre tu contrato.',
  'Gracias por acompañarnos en Habitat 99.',
  'Atención: esto requiere tu acción antes de 48 hs.',
];

const MAX_CHARACTERS = 480;

const MessageComposer = ({ message, preview, onMessageChange, onAddEmoji }) => {
  const charactersLeft = MAX_CHARACTERS - (message.body?.length || 0);
  const [emojiAnchor, setEmojiAnchor] = useState(null);

  const handleEmojiSelect = (emoji) => {
    const value = emoji?.native || '';
    if (value) {
      onAddEmoji(value);
    }
    setEmojiAnchor(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Redacción del push
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Armá el mensaje como si fuera un bloque de blog: título, cuerpo rico en emojis y peso
            visual.
          </Typography>
        </Box>

        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <Box flex={1}>
            <CustomTextField
              label="Título corto"
              value={message.title}
              onChange={(event) => onMessageChange('title', event.target.value)}
              placeholder="Ej: Nueva actualización de Habitat 99"
            />
          </Box>
        </Stack>

        <Box>
          <CustomTextArea
            label="Cuerpo del mensaje"
            value={message.body}
            onChange={(event) => onMessageChange('body', event.target.value.slice(0, MAX_CHARACTERS))}
            placeholder="Contá la novedad, agregá detalles y emojis."
            minRows={6}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color={charactersLeft < 50 ? 'warning.main' : 'text.secondary'}>
              {charactersLeft} caracteres disponibles
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {EMOJI_PRESETS.map((emoji) => (
                <Chip
                  key={emoji}
                  label={emoji}
                  onClick={() => onAddEmoji(emoji)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '18px',
                    height: 32,
                  }}
                />
              ))}
              <IconButton
                color="primary"
                onClick={(event) => setEmojiAnchor(event.currentTarget)}
                size="small"
              >
                <InsertEmoticonIcon />
              </IconButton>
              <Popover
                open={Boolean(emojiAnchor)}
                anchorEl={emojiAnchor}
                onClose={() => setEmojiAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Picker data={data} onEmojiSelect={handleEmojiSelect} />
              </Popover>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            Peso visual del mensaje
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={message.emphasis}
            onChange={(event, value) => {
              if (value) onMessageChange('emphasis', value);
            }}
            size="small"
          >
            {emphasisOptions.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none' }}>
                {option.icon}
                <Box component="span" sx={{ ml: 1 }}>
                  {option.label}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Frases rápidas
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            {quickSnippets.map((snippet) => (
              <Tooltip key={snippet} title="Agregar al cuerpo">
                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={() => onMessageChange('body', `${message.body} ${snippet}`.trim())}
                  buttonIcon={FormatItalicIcon}
                  iconProps={{ fontSize: 'small' }}
                  sx={{ minWidth: 'auto', px: 2, py: 1, fontSize: 13 }}
                >
                  {snippet}
                </CustomButton>
              </Tooltip>
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Vista previa rápida
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: preview.fontWeight,
                mb: 0.5,
              }}
            >
              {preview.title || 'Título pendiente'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {preview.excerpt || 'Escribí el cuerpo para ver la vista previa.'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <Chip size="small" variant="outlined" label={preview.audienceLabel || 'Sin audiencia'} />
            </Stack>
          </Paper>
        </Box>
      </Stack>
    </Paper>
  );
};

export default MessageComposer;
